import React, { useState, useEffect } from "react";
import {
  Box,
  Link,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BASE_URL from "../../config/apiConfig";
import { isAdminAuthenticated, saveAdminToken } from "../../utils/auth";
import "./LoginPage.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ERROR_MESSAGES = {
  invalidEmail: "Please enter a valid email address",
  shortPassword: "Password must be at least 6 characters",
  invalidCredentials: "Invalid email or password.",
  generalError: "An error occurred. Please try again later.",
};

export function LoginPage() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    server: null,
  });

  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const validateEmail = (value) => emailRegex.test(value);
  const validatePassword = (value) => value.length >= 6;

  const handleSignIn = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErrors({
      email: false,
      password: false,
      server: null,
    });

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      setErrors({
        email: !isEmailValid,
        password: !isPasswordValid,
        server: null,
      });
      setLoading(false);
      return;
    }

    console.log("Initiating admin login process...");

    try {
      const response = await fetch(`${BASE_URL}/api/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();

        console.log("Admin login successful:", data);

        if (!data.token) {
          throw new Error("Token is missing in the response.");
        }

        saveAdminToken(data.token, email);
        navigate("/admin/dashboard");
      } else {
        let errorData = {};

        try {
          errorData = await response.json();
        } catch {
          errorData = {};
        }

        console.error("Admin login error:", errorData.message);

        setErrors((prev) => ({
          ...prev,
          server: errorData.message || ERROR_MESSAGES.invalidCredentials,
        }));
      }
    } catch (error) {
      console.error("Error during admin login request:", error);

      setErrors((prev) => ({
        ...prev,
        server: ERROR_MESSAGES.generalError,
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="farmology-admin-auth-page">
      <Container className="farmology-admin-auth-container">
        <Box className="farmology-admin-auth-card">
          <Box className="farmology-admin-form-panel">
            <Box className="farmology-admin-brand">
              <Box className="farmology-admin-logo-wrap">
                <img
                  src="/Images/Farmology_Logo.webp"
                  alt="Farmology"
                  className="farmology-admin-logo"
                />
              </Box>

              <Typography className="farmology-admin-eyebrow">
                FARMOLOGY ADMIN
              </Typography>

              <Typography component="h1" className="farmology-admin-title">
                Admin sign in
              </Typography>

              <Typography className="farmology-admin-description">
                Sign in to manage the Farmology platform, monitor operations and
                keep your agricultural intelligence running smoothly.
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSignIn}
              className="farmology-admin-form"
            >
              <TextField
                fullWidth
                name="email"
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                helperText={errors.email ? ERROR_MESSAGES.invalidEmail : ""}
                variant="outlined"
                className="farmology-admin-field"
              />

              <TextField
                fullWidth
                name="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                error={errors.password}
                helperText={errors.password ? ERROR_MESSAGES.shortPassword : ""}
                variant="outlined"
                className="farmology-admin-field"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        className="farmology-admin-password-toggle"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box className="farmology-admin-forgot-row">
                <Link
                  href="/user/recoverpassword"
                  className="farmology-admin-forgot-link"
                >
                  Forgot password?
                </Link>
              </Box>

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                className="farmology-admin-submit"
              >
                Sign In
                <span className="farmology-admin-arrow">↗</span>
              </LoadingButton>

              {errors.server && (
                <Typography
                  className="farmology-admin-error"
                  aria-live="polite"
                >
                  {errors.server}
                </Typography>
              )}
            </Box>

            <Typography className="farmology-admin-footer-note">
              © {new Date().getFullYear()} Farmology. Admin access portal.
            </Typography>
          </Box>

          <Box className="farmology-admin-visual-panel">
            <Box className="farmology-admin-visual-copy">
              <Typography className="farmology-admin-visual-eyebrow">
                SMART AGRICULTURE
              </Typography>

              <Typography
                component="h2"
                className="farmology-admin-visual-title"
              >
                Manage smarter.
                <br />
                Farm better.
              </Typography>

              <Typography className="farmology-admin-visual-text">
                One platform for the data, services and decisions that power
                modern farming.
              </Typography>
            </Box>

            <Box className="farmology-admin-image-wrap">
              <img
                src="/Images/Login_Body.webp"
                alt="Farmology agriculture illustration"
                className="farmology-admin-image"
              />
            </Box>

            <Box className="farmology-admin-stat-card">
              <span className="farmology-admin-stat-label">ADMIN CONSOLE</span>
              <strong>Control your platform</strong>
            </Box>

            <Box className="farmology-admin-circle" />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default LoginPage;
